import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import {
  growthBookInstances,
  resolveContentBranch,
  setQuizMediaOverride,
} from '../constants';
import {
  getVariation,
  applyContentSfwOverride,
  VARIATION_SFW,
} from '../utils/variation';
import {
  growthbook,
  gbReady,
  gbCountryReady,
  getGbCountryCode,
} from '../lib/growthbookClient';
import { setLanderContent } from '../utils/analytics';
import {
  PAYWALL_NAV_CONFIG_FLAG,
  DEFAULT_NAV_CONFIG,
  resolveNavConfig,
} from '../utils/authType';
import {
  FULL_REBILL_FLAG,
  FULL_REBILL_CONTROL,
  LOCATION_PRICE_MULTIPLIER_FLAG,
  DEFAULT_PRICE_MULTIPLIER,
  isPopupFirst,
} from '../utils/paywall';

// Ceiling on the arm gate below. The SFW force rule is targeted on `countryCode`
// (GB rule fr_mta6vf82: UK/GB), and that attribute only lands after the
// /auth/country-code round-trip — measured 130-390ms, with the GB payload itself
// at 220-280ms. Rendering before both settle shows a forced-SFW visitor the NSFW
// media for that window, so the gate waits; the ceiling keeps a slow or failed
// lookup from holding the funnel hostage.
const ARM_GATE_TIMEOUT_MS = 5000;

const GrowthBookContext = createContext(null);

export const GrowthBookProvider = ({ children }) => {
  const isNDG = useMemo(() => window.location.pathname.includes('/ndg'), []);

  // Whether the NSFW/SFW arm can be trusted yet — see ARM_GATE_TIMEOUT_MS. The tree
  // still renders immediately; consumers that would otherwise flash the wrong arm's
  // media (Step0's two cards) show a skeleton until this flips.
  //
  // `gbCountryReady` is only awaited when the country is not already known: it
  // re-fetches on every load, while getGbCountryCode() reads this tab's cached
  // value, so a second page view opens the gate on `gbReady` alone.
  const [armResolved, setArmResolved] = useState(false);
  useEffect(() => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setArmResolved(true);
    };
    const waits = getGbCountryCode() ? [gbReady] : [gbReady, gbCountryReady];
    Promise.all(waits).then(finish, finish);
    const timer = setTimeout(finish, ARM_GATE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // NSFW (default) or SFW (`?variation=sfw`) — see utils/variation. Resolved
  // once per mount: the URL arm cannot change mid-visit.
  const urlVariation = useMemo(() => getVariation(), []);

  // Defaulted to `null` (not the built-in map) so resolveContentBranch can tell
  // "flag absent" from "flag returned the NSFW defaults" and pick the right
  // per-variation defaults in the first case.
  const rawContent = useFeatureValue('createsexfriend-content', null);

  // `isSFWOverride: true` on the content flag forces the SFW arm without
  // `?variation=sfw`. Pushed into utils/variation during render (not in an
  // effect) so the first render that sees the flag already resolves the SFW
  // content/attribute branches, and so the non-React handoff helpers
  // (applyProtection, useUpgateCheckout) read the forced arm too.
  const sfwOverride = useMemo(
    () => applyContentSfwOverride(rawContent),
    [rawContent],
  );

  const variation = sfwOverride ? VARIATION_SFW : urlVariation;
  const isSfw = variation === VARIATION_SFW;

  // Accepts both the legacy flat map and the branched { nsfw, sfw } shape.
  const content = useMemo(
    () => resolveContentBranch(rawContent, variation),
    [rawContent, variation],
  );

  // Debug: what GrowthBook actually returned for createsexfriend-content, and
  // what the active variation resolved it to. Runs on settle (the flag arrives
  // async, so the first render logs `null` before the payload lands).
  useEffect(() => {
    const attrs = growthbook.getAttributes();
    // Which rule GB actually picked. `source: 'force'` + the sfw rule id means the
    // targeting matched; 'experiment' means an earlier rule won and the sfw force
    // rule was never reached.
    let evaluated = null;
    try {
      const r = growthbook.evalFeature('createsexfriend-content');
      evaluated = { source: r?.source, ruleId: r?.ruleId };
    } catch (e) {
      evaluated = { error: String(e) };
    }
    console.log('[createsexfriend-content]', {
      variationResolvedByApp: variation,
      // true → the arm was forced by the flag's isSFWOverride, not by the URL.
      sfwForcedByFlag: sfwOverride,
      variationAttributeInSdk: attrs?.variation,
      // These must be identical. If the SDK one is undefined/'nsfw' while the app
      // says 'sfw', the attribute was clobbered after init and the GB rule cannot match.
      attributesMatch: attrs?.variation === variation,
      evaluated,
      shape:
        rawContent === null || rawContent === undefined
          ? 'absent (using built-in defaults — GB payload has not landed yet)'
          : rawContent.nsfw || rawContent.sfw
            ? 'branched { nsfw, sfw }'
            : 'legacy flat map',
      raw: rawContent,
      resolved: content,
      // createsexfriend-attributes drives the quiz ATTRIBUTE steps (ethnicity,
      // bodyType, ...). A 'legacy flat map' here means it is applied to BOTH arms
      // unchanged, so the SFW arm keeps whatever paths that value carries.
      attributesFlag:
        quizAttributes === null || quizAttributes === undefined
          ? 'absent (built-in defaults)'
          : quizAttributes.nsfw || quizAttributes.sfw
            ? 'branched { nsfw, sfw }'
            : 'legacy flat map (SAME on both arms)',
    });
  }, [rawContent, content, variation, sfwOverride]);
  const paywallTheme = useFeatureValue(
    'createsexfriends-paywall-theme',
    'default',
  );
  const animatedPictures = useFeatureValue(
    'createsexfriend-animated-pictures',
    'AUTOMATIC',
  );
  const steps = useFeatureValue('createsexfriend-steps', 'BASE');
  const attributeSelectionStyle = useFeatureValue(
    'createsexfriend-attribute-selection-style',
    'DEFAULT',
  );
  const postSignupRedirect = useFeatureValue(
    'createsexfriends-post-signup-redirect',
    'OFF',
  );
  const magicLink = useFeatureValue('createsexfriends-magicLink', 'OFF');
  // GB-controlled header video for the paywall popup.
  const paywallPopupVideo = useFeatureValue('paywall_popup_video', '');
  // Platform-global token-bonus experiments driving the paywall popup's heart pill
  // (same flag names/defaults as useTokenBonus + the platform). Exposed here so the
  // auth flow can forward them in `ab_testing` for per-variant conversion attribution.
  const premiumTokensAmount = useFeatureValue('premium-tokens-amount', '');
  const premiumInstantTokens = useFeatureValue(
    'premium-instant-tokens-amount',
    '0|0|0',
  );
  // Resolved rebill arm — drives the Upgate fine print (see utils/paywall).
  const fullRebill = useFeatureValue(FULL_REBILL_FLAG, FULL_REBILL_CONTROL);
  // Geo price multiplier — the plans the LP fetches are unmultiplied, so the
  // Upgate fine print has to apply it (see utils/paywall).
  const locationPriceMultiplier = useFeatureValue(
    LOCATION_PRICE_MULTIPLIER_FLAG,
    DEFAULT_PRICE_MULTIPLIER,
  );
  const summaryVariations = useFeatureValue(
    'createsexfriend-summary-variations',
    'control',
  );
  const isPersonalityRaw = useFeatureValue(
    'createsexfriend-isPersonality',
    false,
  );
  // Normalize to a strict boolean (GrowthBook may hand back "true"/"false" strings).
  const isPersonality =
    isPersonalityRaw === true || isPersonalityRaw === 'true';
  // Quiz attribute MEDIA overrides (videos/images per Realistic|Anime mode),
  // overridable via GrowthBook. `null` default → built-in asset paths are kept.
  const quizAttributes = useFeatureValue('createsexfriend-attributes', null);

  // Apply the override during render (synchronously, before children render) so
  // every getQuizOptionsData() reader picks it up on all routes.
  useMemo(
    () => setQuizMediaOverride(quizAttributes, variation),
    [quizAttributes, variation],
  );

  // Post-auth navigation config (JSON flag: { type, flow }).
  //   type 'paywall'       → existing behaviour (redirect to /paywall);
  //   type 'chat_paywall'  → redirect to /chats, model-params carried in query;
  //   type 'paywall_popup' → NEW: open the in-place paywall popup after signup
  //                          (no redirect); the popup → Upgate → /lobby flow.
  //   type 'simple_registration' → AB test (86ca50q0c): no email on the LP. On the
  //                          summary step a dummy user is created, then the user is
  //                          sent to /paywall with auth_flow=tempLogin; the real
  //                          email is collected on the platform checkout.
  //   flow 'chat'|'generation' → only meaningful for paywall_popup; tells /lobby
  //                          where to route after payment. Defaults to 'generation'.
  //   order 'popup_first'|'after_signup' → only meaningful for paywall_popup;
  //                          'popup_first' renders the popup BEFORE registration.
  //                          Defaults to 'after_signup' (the existing behaviour).
  // We expose the resolved `type` + `flow` strings downstream (they ride along
  // in ab_testing so the backend stores them in the registration Redis payload).
  const paywallNavConfig = useFeatureValue(
    PAYWALL_NAV_CONFIG_FLAG,
    DEFAULT_NAV_CONFIG,
  );

  // Sticky (per-tab) resolution — see utils/authType. everflow resolves the same
  // way, so the arm reported as sub7 always matches the arm the funnel runs.
  const resolvedNavConfig = useMemo(
    () => resolveNavConfig(paywallNavConfig),
    [paywallNavConfig],
  );
  const paywallNavType = resolvedNavConfig?.type || 'paywall';
  const paywallFlow = resolvedNavConfig?.flow || 'generation';
  // order 'popup_first' → NEW "popup first" A/B arm: render the paywall popup
  // BEFORE registration (see utils/paywall.js isPopupFirst). Only meaningful when
  // type === 'paywall_popup'; the backend nav `type` is unchanged so nothing
  // downstream (backend Google return, platform /lobby routing) needs to know it.
  const paywallOrder = resolvedNavConfig?.order || 'after_signup';

  // Forward the content config's `name` to analytics as `lander-content`, so it
  // rides along on every GTM event regardless of which variation is shown.
  useEffect(() => {
    if (content?.name) setLanderContent(content.name);
  }, [content]);

  // Keep GrowthBook's `variation` attribute in step with a forced SFW arm, so flags
  // targeted on the arm — paywall_popup_video among them — evaluate against the arm
  // actually being rendered. Guarded on the ATTRIBUTE, not on getVariation(): the
  // override is already latched during render, so isSfwVariation() would be true
  // here on the very first run and the sync would never happen.
  useEffect(() => {
    if (!sfwOverride) return;
    if (growthbook.getAttributes()?.variation === VARIATION_SFW) return;
    try {
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        variation: VARIATION_SFW,
      });
    } catch {
      /* attribute sync is best-effort — the rendered arm is already correct */
    }
  }, [sfwOverride]);

  const values = useMemo(
    () => ({
      content: content || growthBookInstances,
      paywallTheme: paywallTheme || 'default',
      animatedPictures: animatedPictures || 'AUTOMATIC',
      steps: steps || 'BASE',
      attributeSelectionStyle: attributeSelectionStyle || 'DEFAULT',
      postSignupRedirect: postSignupRedirect || 'OFF',
      magicLink: magicLink || 'OFF',
      summaryVariations: summaryVariations || 'control',
      paywallNavType: paywallNavType || 'paywall',
      paywallFlow: paywallFlow || 'generation',
      paywallOrder: paywallOrder || 'after_signup',
      paywallPopupVideo: paywallPopupVideo || '',
      premiumTokensAmount: premiumTokensAmount ?? '',
      premiumInstantTokens: premiumInstantTokens ?? '0|0|0',
      fullRebill,
      locationPriceMultiplier,
      isPersonality,
      isNDG,
      variation,
      isSfw,
      armResolved,
    }),
    [
      content,
      paywallTheme,
      animatedPictures,
      steps,
      attributeSelectionStyle,
      postSignupRedirect,
      magicLink,
      summaryVariations,
      paywallNavType,
      paywallFlow,
      paywallOrder,
      paywallPopupVideo,
      premiumTokensAmount,
      premiumInstantTokens,
      fullRebill,
      locationPriceMultiplier,
      isPersonality,
      isNDG,
      variation,
      isSfw,
      armResolved,
    ],
  );

  return (
    <GrowthBookContext.Provider value={values}>
      {children}
    </GrowthBookContext.Provider>
  );
};

export const useGrowthBookValues = () => {
  const context = useContext(GrowthBookContext);
  if (!context) {
    throw new Error(
      'useGrowthBookValues must be used within GrowthBookProvider',
    );
  }
  return context;
};
